import { useState, useEffect } from 'react';

import Button from '@/components/Button/Button';
import SkeletonLoader from '@/components/SkeletonLoader';
import { Switch } from '@/components/Switch';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getTeacherPreferences,
  updateTeacherPreferences,
} from '@/service/settingsService';

const Preferences = () => {
  const { formatText } = useFormatText();
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      setIsLoading(true);
      try {
        const res = await getTeacherPreferences();
        if (res?.status && res?.data) {
          setEmailEnabled(res.data.weekly_summary_email ?? false);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        weekly_summary_email: emailEnabled,
      };
      await updateTeacherPreferences(payload);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-216 flex flex-col gap-6">
      {/* Page Title */}
      <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
        {formatText('settings.preferences.title')}
      </h2>

      <div className="flex flex-col gap-6">
        {/* Preference Card */}
        <div className="p-4 border border-neutral-light-200 rounded-2xl bg-white">
          <div className="flex items-center justify-between gap-12">
            <div className="flex flex-col gap-1 flex-1">
              <span className="text-base font-semibold text-neutral-primary leading-6">
                {formatText('settings.preferences.weekly-summary-email')}
              </span>
              <span className="text-sm font-normal text-neutral-secondary leading-5">
                {formatText(
                  'settings.preferences.weekly-summary-email-description'
                )}
              </span>
            </div>

            {/* Switch */}
            {isLoading ? (
              <SkeletonLoader width={44} height={24} className="rounded-full" />
            ) : (
              <Switch
                checked={emailEnabled}
                onChange={(checked) => setEmailEnabled(checked)}
                id="preferences-weekly-summary-email"
              />
            )}
          </div>
        </div>

        {/* Save Changes Button */}
        <div className="flex justify-start">
          <Button
            label={formatText('common.actions.save-changes')}
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={isLoading || isSaving}
          />
        </div>
      </div>
    </div>
  );
};

export default Preferences;
